/**
 * Le raccord entre le cockpit et le monde de démonstration.
 *
 * Deux choses, et rien d'autre :
 *   - `DemoBridge` verse dans la démo ce qu'elle ne peut pas inventer — les
 *     VRAIES créatrices du compte connecté, et l'identité de qui chatte — puis
 *     invalide les caches pour que la grille reparte sur le monde neuf.
 *   - `DemoBadge` pose la pastille qui dit qu'on n'est pas en prod.
 *
 * Monté au shell (comme `ConversationLockController`), il rend `null` hors
 * mode démo : le cockpit ordinaire ne paie rien pour lui.
 */
import { useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Badge, Infobulle, useConfirmer } from "@dozka/ui";

import { api as cyranoApi } from "@/core/api/client";
import { useModeDemo, useModeDemoToggle } from "@/core/demo";
import { oublierLargent } from "@/core/demo/argent";
import { oublierLeClassement } from "@/core/demo/classement";
import { abonner } from "@/core/demo/bus";
import { oublierLesFlows } from "@/core/demo/flows";
import { oublierLeMonde, poserLesCreatrices, poserLoperatrice } from "@/core/demo/monde";
import { lancerLaVieDeFond, oublierLaVieDeFond } from "@/core/demo/scenario";
import { useSession } from "@/core/auth/useSession";
import { useCreatorsList } from "@/panels/creators/atoms";

/** `GET /api/creators` — la route faite pour résoudre `slug → id Cyrano`. */
interface ReponseCreatrices {
  creators: { id: string; slug: string }[];
}

export function DemoBridge() {
  const enDemo = useModeDemo();
  const creatrices = useCreatorsList();
  const { user } = useSession();
  const queryClient = useQueryClient();

  // 🔑 L'identifiant CYRANO de chaque créatrice. Sans lui, les routes qui ne
  // sont pas détournées par la démo (Vault, flows, emojis) reçoivent un id
  // Convex et répondent 4xx — le compositeur ouvre alors un Vault vide.
  // Cette route N'EST PAS détournée : c'est de la vraie configuration.
  const idsCyrano = useQuery({
    queryKey: ["demo", "creators-cyrano"],
    queryFn: () => cyranoApi.get<ReponseCreatrices>("/api/creators"),
    enabled: enDemo && user !== null,
    staleTime: 10 * 60_000,
    retry: 1,
  });

  const parSlug = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of idsCyrano.data?.creators ?? []) {
      map.set(c.slug.trim().toLowerCase(), c.id);
    }
    return map;
  }, [idsCyrano.data]);

  // On attend la réponse (ou son échec) avant de bâtir : bâtir puis rebâtir
  // avec d'autres identifiants ferait clignoter la grille et jetterait les
  // conversations déjà ouvertes.
  const idsConnus = idsCyrano.isSuccess || idsCyrano.isError;

  useEffect(() => {
    if (!enDemo) {
      oublierLeMonde();
      oublierLaVieDeFond();
      oublierLesFlows();
      oublierLargent();
      oublierLeClassement();
      return;
    }
    poserLoperatrice({
      workosUserId: user?.workosUserId ?? null,
      email: user?.email ?? null,
    });
    if (creatrices.length === 0 || !idsConnus) return;
    const reconstruit = poserLesCreatrices(
      creatrices.map((c) => ({
        // L'id Cyrano quand on l'a ; l'id Convex sinon (la grille marche, les
        // routes créatrice non — mieux que pas de démo du tout).
        id: parSlug.get(c.slug.trim().toLowerCase()) ?? c.id,
        slug: c.slug,
        nom: c.name,
        avatar_url: c.avatar_url ?? null,
      })),
    );
    if (reconstruit) {
      // Le monde a changé sous les pieds des requêtes déjà servies — on les
      // refait plutôt que d'afficher un mélange de deux mondes.
      void queryClient.invalidateQueries({ queryKey: ["conversations"] });
      void queryClient.invalidateQueries({ queryKey: ["fan-tags"] });
      lancerLaVieDeFond();
    }
  }, [enDemo, creatrices, user, queryClient, parSlug, idsConnus]);

  // « Discussions » se relit toutes les 30 s au poste. En démo, un fan qui
  // ARRIVE doit y apparaître sous les yeux du chatteur, pas une demi-minute
  // plus tard : chaque événement du monde fait relire les recommandations
  // et la file (au plus une fois par seconde).
  useEffect(() => {
    if (!enDemo) return;
    let enAttente: ReturnType<typeof setTimeout> | null = null;
    const desabonner = abonner((e) => {
      const type = (e as { type?: string }).type;
      if (type !== "new_message" && type !== "conversation_state_changed") return;
      if (enAttente) return;
      enAttente = setTimeout(() => {
        enAttente = null;
        void queryClient.invalidateQueries({ queryKey: ["conversations", "recommandations"] });
        void queryClient.invalidateQueries({ queryKey: ["conversations", "list"] });
      }, 1_000);
    });
    return () => {
      desabonner();
      if (enAttente) clearTimeout(enAttente);
    };
  }, [enDemo, queryClient]);

  return null;
}

/**
 * La pastille « DÉMO ».
 *
 * Volontairement DISCRÈTE : elle est dans le champ de la caméra, et une
 * bannière rouge en travers de l'écran gâcherait exactement ce qu'on essaie de
 * filmer — un cockpit qui a l'air vrai. Mais elle ne peut pas être absente :
 * sans elle, rien à l'écran ne distingue une conversation fictive d'une vraie,
 * et c'est le genre de confusion qui coûte une vente.
 */
export function DemoBadge() {
  const [enDemo, poserLaDemo] = useModeDemoToggle();
  const { confirmer, dialogue } = useConfirmer();
  if (!enDemo) return null;
  // Cliquable depuis le 07-10-2026 : la case « Mode démo » vivait dans
  // « Avancé », retiré à la demande de Kayla. Sans ce clic, on ne sortait plus
  // de la démo qu'en tapant `?demo=0`. On l'allume toujours par `?demo=1`.
  async function quitter() {
    if (!(await confirmer({ titre: "Quitter le mode démo ?", texte: "La page va se recharger." }))) return;
    poserLaDemo(false);
    window.setTimeout(() => window.location.reload(), 50);
  }
  return (
    <>
      <Infobulle
        texte="Mode démo : les conversations sont fictives, aucun message ne part sur Telegram. Clique pour quitter la démo."
        cote="bas"
      >
        <button
          type="button"
          onClick={() => void quitter()}
          className="pointer-events-auto shrink-0 rounded-full outline-none focus-visible:ring-2 focus-visible:ring-line-strong"
        >
          <Badge ton="ambre" point>
            Démo
          </Badge>
        </button>
      </Infobulle>
      {dialogue}
    </>
  );
}
